import { ColumnDef } from '@tanstack/react-table';
import React, { ChangeEvent } from 'react';
import { Form, FormGroup } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useFetchOperation } from '../../service/Operation';
import Alerts from '../general/Alerts';
import AutoPaginationTable from '../general/AutoPaginationTable';
import Error from '../general/Error';
import Loader from '../general/Loader';
import ProgressButton from '../general/ProgressButton';
import { CardHandler, Dictionary } from './../../types/Common';
import DicPanel from './DicPanel';
import { Card } from './../../types/Common';

interface HandleDictionaryPage {
    dictionary: Dictionary,
    cardHandlerId?: number
    cardHandlers: CardHandler[]
}

interface HandleDictionaryResponseDto {
    changed: number,
    compatible: number,
    processed: number,
    duplicates: number,
    invalid: number,
    total: number,
    requestedCards: HandledCard[]
}

interface HandledCard extends Card {
    isCompatible: boolean,
    isChanged: boolean,
    isDuplicate: boolean,
    isInvalid: boolean,
}

export default function HandleCards() {
    const params = useParams();

    const [dictionary, setDictionary] = React.useState<Dictionary>();
    const [cardHandlers, setCardHandlers] = React.useState<CardHandler[]>([]);
    const [cardHandlerId, setCardHandlerId] = React.useState<number>();
    const [emptyPronunciation, setEmptyPronunciation] = React.useState(false);
    const [emptyForms, setEmptyForms] = React.useState(false);
    const [emptyExample, setEmptyExample] = React.useState(false);
    const [handleResponse, setHandleResponse] = React.useState<HandleDictionaryResponseDto>();
    const [limit, setLimit] = React.useState(0);
    const [limitType, setLimitType] = React.useState('First');

    const [getting, startGetting] = useFetchOperation(onGetSuccess, undefined, true);
    const [editing, startEditing, resetEditing] = useFetchOperation(setHandleResponse);

    React.useEffect(() => {
        startGetting('get', `api/dictionaries/${params.id}/handle`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    function onGetSuccess(data: HandleDictionaryPage) {
        setDictionary(data.dictionary);
        setCardHandlers(data.cardHandlers);
        setCardHandlerId(data.dictionary.cardHandlerId ?? data.cardHandlers[0].id);
        document.title = `Beautify cards - ${data.dictionary.name} - Dictionaries - ${(document as any).rootTitle}`;
    }

    function onHandlerChange(e: ChangeEvent<HTMLSelectElement>) {
        setCardHandlerId(parseInt(e.target.value));
    }

    function onEmptyPronunciationChange(e: React.ChangeEvent<HTMLInputElement>) {
        setEmptyPronunciation(e.target.checked);
    }

    function onEmptyFormsChange(e: React.ChangeEvent<HTMLInputElement>) {
        setEmptyForms(e.target.checked);
    }

    function onEmptyExampleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setEmptyExample(e.target.checked);
    }

    function onLimitChange(e: React.ChangeEvent<HTMLInputElement>) {
        setLimit(e.target.value ? parseInt(e.target.value) : 0);
    }

    function onLimitTypeChange(e: ChangeEvent<HTMLInputElement>) {
        setLimitType(e.target.value);
    }


    async function onHandleClick() {
        setHandleResponse(undefined);
        const body = {
            dictionaryId: dictionary!.id,
            cardHandlerId,
            emptyPronunciation,
            emptyForms,
            emptyExample,
            limit,
            limitType
        };
        startEditing('post', `api/dictionaries/handle`, body);
    }

    const successContent = `${handleResponse?.changed} cards changed (${handleResponse?.compatible} compatible, ${handleResponse?.duplicates} duplicates, ${handleResponse?.invalid} invalid) from ${handleResponse?.processed} selected from ${handleResponse?.total} total cards`;

    return (
        <>
            <DicPanel dictionary={dictionary} />

            {getting.active ?
                <Loader />
                :
                !cardHandlers?.length ?
                    <Error text={getting.error} />
                    :
                    <div className="w-100">
                        <h2>Beautify cards</h2>

                        <div className="width-limit">
                            <Form>
                                <FormGroup>
                                    <Form.Label htmlFor="handlerId">Handler</Form.Label>
                                    <Form.Select id="handlerId"
                                        className="py-1"
                                        value={cardHandlerId}
                                        onChange={onHandlerChange}>
                                        {cardHandlers.map(x =>
                                            <option value={x.id} key={x.id}>{x.name}</option>)}
                                    </Form.Select>
                                </FormGroup>

                                <FormGroup>
                                    <Form.Check
                                        type="checkbox"
                                        label="Only with empty Pronunciation"
                                        className="text-start my-3"
                                        checked={emptyPronunciation}
                                        onChange={onEmptyPronunciationChange} />
                                </FormGroup>

                                <FormGroup>
                                    <Form.Check
                                        type="checkbox"
                                        label="Only with empty Forms"
                                        className="text-start my-3"
                                        checked={emptyForms}
                                        onChange={onEmptyFormsChange} />
                                </FormGroup>

                                <FormGroup>
                                    <Form.Check
                                        type="checkbox"
                                        label="Only with empty Example"
                                        className="text-start my-3"
                                        checked={emptyExample}
                                        onChange={onEmptyExampleChange} />
                                </FormGroup>

                                <FormGroup className="text-start my-3">
                                    <span>Limit:</span>
                                    <Form.Control type="number"
                                        className="d-inline-block mx-2"
                                        onChange={onLimitChange}
                                        value={limit > 0 ? limit : ''}
                                        placeholder="no limit"
                                        style={{ width: '100px' }} />

                                    <Form.Check type="radio"
                                        className="d-inline-block mx-2"
                                        label="first"
                                        checked={limitType === 'First'}
                                        value="First"
                                        onChange={onLimitTypeChange} />

                                    <Form.Check type="radio"
                                        className="d-inline-block mx-2"
                                        label="last"
                                        checked={limitType === 'Last'}
                                        value="Last"
                                        onChange={onLimitTypeChange} />

                                    <Form.Check type="radio"
                                        className="d-inline-block mx-2"
                                        label="your weakest"
                                        checked={limitType === 'Weakest'}
                                        value="Weakest"
                                        onChange={onLimitTypeChange} />
                                </FormGroup>

                                <div className="panel">
                                    <ProgressButton className="d-block mx-auto btn-wide panel-btn"
                                        onClick={onHandleClick}
                                        active={editing.active}
                                        text="Beautify"
                                        success={editing.success} />
                                </div>
                            </Form>

                            <Alerts success={editing.success}
                                error={editing.error}
                                onClose={() => resetEditing()}
                                successContent={successContent} />
                        </div>

                        {handleResponse?.requestedCards &&
                            <AutoPaginationTable className="my-3"
                                columns={columns}
                                data={handleResponse.requestedCards} />
                        }
                    </div>
            }
        </>
    )
}

const columns: ColumnDef<HandledCard>[] = [
    {
        header: '#',
        accessorFn: c => c.id,
        cell: props => <Link to={`/cards/${props.row.original.id}`}> {props.row.index + 1}</Link>
    },
    {
        header: 'Phrase',
        accessorFn: c => c.phrase,
    },
    {
        header: 'Meaning',
        accessorFn: c => c.meaning,
    },
    {
        header: 'Pronunciation',
        accessorFn: c => c.pronunciation,
    },
    {
        header: 'Forms',
        accessorFn: c => c.forms,
    },
    {
        header: 'Example',
        accessorFn: c => c.example,
    },
    {
        header: 'Req.',
        accessorFn: c => c.isCompatible,
        cell: props => <span>{props.row.original.isCompatible && "✓"}</span>
    },
    {
        header: 'Ch.',
        accessorFn: c => c.isChanged,
        cell: props => <span>{props.row.original.isChanged && "✓"}</span>
    },
    {
        header: 'Dupl.',
        accessorFn: c => c.isDuplicate,
        cell: props => <span>{props.row.original.isDuplicate && "✓"}</span>
    },
    {
        header: 'Inv.',
        accessorFn: c => c.isInvalid,
        cell: props => <span>{props.row.original.isInvalid && "✓"}</span>
    },
]
